"use strict";

exports.__esModule = true;
exports.RenderDataToComponents = void 0;
var _wixDataSchemaTypes = require("@wix/wix-data-schema-types");
var _errors = require("../common/errors");
var _dataset = require("../entities/dataset");
var _schema = require("../entities/schema");
var _config = require("../entities/config");
var _record = require("../entities/record");
var _value = require("../entities/value");
//TODO: Inline constant - issues with tree-shaking of wix-data-client-common
const DATETIME_FORMAT_TYPE = 'datetime';
const RenderDataToComponents = ({
  config,
  formatter,
  dataStore,
  errorReporter
}) => ({
  dataset,
  connections,
  components
}) => {
  const schema = dataStore.readSchema(dataset.collectionId);
  const referencedCollectionIds = _schema.Schema.getReferencedCollectionIds(schema);
  const referencedSchemas = dataStore.readSchemas(referencedCollectionIds);
  const records = dataStore.readRecords(_dataset.Dataset.getReadOptions(dataset));
  const connectionsToUpdatedComponents = _config.Config.filterConnectionsByUpdatedComponents(config, connections);
  for (const connection of connectionsToUpdatedComponents) {
    const propEntries = mapValuesToComponentProps({
      config,
      connection,
      dataset,
      schema,
      referencedSchemas,
      records
    }, {
      formatter
    });
    const component = components.find(comp => comp.id === connection.compId);
    if (propEntries.length) {
      try {
        component.setData(Object.fromEntries(propEntries));
      } catch (error) {
        errorReporter.report(_errors.AppError.of('Failed setting data to component', {
          cause: error
        }));
      }
    }
  }
};
exports.RenderDataToComponents = RenderDataToComponents;
const mapValuesToComponentProps = ({
  config,
  connection,
  dataset,
  schema,
  referencedSchemas,
  records
}, {
  formatter
}) => {
  const componentProps = [...getDynamicProps({
    config,
    connection,
    dataset,
    schema,
    referencedSchemas,
    records
  }, {
    formatter
  }), ...getBehaviorRelatedProps(connection, dataset), ...getExtraProps(connection)];
  for (const [name, connectionDescriptor] of Object.entries(connection.properties)) {
    if (connectionDescriptor.properties) {
      const nestedComponentPropEntries = mapValuesToComponentProps({
        config,
        connection: connectionDescriptor,
        dataset,
        schema,
        referencedSchemas,
        records
      }, {
        formatter
      });
      componentProps.push([name, Object.fromEntries(nestedComponentPropEntries)]);
    } else {
      const currentRecordIndex = _dataset.Dataset.getCurrentRecordIndex(dataset);
      const currentRecord = records.at(currentRecordIndex);
      if (currentRecord) {
        const {
          fieldName,
          dataType,
          format
        } = connectionDescriptor;
        const {
          value,
          fieldType
        } = getFieldData({
          currentRecord,
          fieldName,
          schema,
          referencedSchemas
        });
        if (shouldSetValue(config, value)) {
          const valuePresentation = getValuePresentation({
            format,
            fieldType,
            dataType,
            value
          }, {
            formatter
          });
          componentProps.push([name, valuePresentation]);
        }
      }
    }
  }
  return componentProps;
};
const dataTypeToConverter = {
  text: ({
    fieldType,
    value
  }) => {
    if (value === null || value === undefined) {
      return '';
    } else if (Array.isArray(value)) {
      return value.join(', ');
    } else if (fieldType === _wixDataSchemaTypes.FieldType.address) {
      return typeof value === 'object' && 'formatted' in value ? value.formatted : value;
    } else {
      return String(value);
    }
  }
};
const behaviorRelatedPropSetters = {
  input: ({
    disabled: disabledProp
  }, dataset) => _dataset.Dataset.isReadonly(dataset) ? [disabledProp, true] : [],
  click: ({
    action,
    disabled: disabledProp
  }, dataset) => action === 'next' && !_dataset.Dataset.hasNextRecord(dataset) || action === 'previous' && !_dataset.Dataset.hasPrevRecord(dataset) ? [disabledProp, true] : []
};
const dynamicPropsSetters = {
  text: ({
    config,
    dataset,
    schema,
    referencedSchemas,
    records,
    connectionDescriptor: {
      fieldName,
      typeMapping,
      extraData,
      propName,
      format
    }
  }, {
    formatter
  }) => {
    const currentRecordIndex = _dataset.Dataset.getCurrentRecordIndex(dataset);
    const currentRecord = records.at(currentRecordIndex);
    if (!currentRecord) {
      return undefined;
    }
    const {
      value,
      fieldType
    } = getFieldData({
      currentRecord,
      fieldName,
      schema,
      referencedSchemas
    });
    if (shouldSetValue(config, value)) {
      return [typeMapping[fieldType] ?? propName, fieldType === _wixDataSchemaTypes.FieldType.url ? makeLinkFromText(value, extraData) : getValuePresentation({
        format,
        dataType: 'text',
        fieldType,
        value
      }, {
        formatter
      })];
    } else {
      return undefined;
    }
  },
  totalCount: ({
    dataset,
    connectionDescriptor: {
      propName
    }
  }) => {
    if (dataset.pagination.type === 'cursor') {
      return undefined;
    }
    return [propName, String(_dataset.Dataset.getTotalCount(dataset))];
  }
};
const convertValueByDataType = ({
  value,
  fieldType,
  dataType
}) => {
  const convert = dataTypeToConverter[dataType] ?? (({
    value
  }) => value);
  return convert({
    fieldType,
    value
  });
};
const getValuePresentation = ({
  dataType,
  format,
  fieldType,
  value
}, {
  formatter
}) => {
  const formattedValue = formatValue({
    format,
    formatter,
    fieldType,
    value
  });
  const convertedValue = convertValueByDataType({
    dataType,
    value: formattedValue,
    fieldType
  });
  return convertedValue;
};
const getBehaviorRelatedProps = (connection, dataset) => {
  const behaviorRelatedProps = [];
  for (const [name, behaviorDescriptor] of Object.entries(connection.behaviors)) {
    var _behaviorRelatedPropS;
    const behaviorRelatedPropEntry = (_behaviorRelatedPropS = behaviorRelatedPropSetters[name]) == null ? void 0 : _behaviorRelatedPropS.call(behaviorRelatedPropSetters, behaviorDescriptor, dataset);
    if (behaviorRelatedPropEntry != null && behaviorRelatedPropEntry.length) {
      behaviorRelatedProps.push(behaviorRelatedPropEntry);
    }
  }
  return behaviorRelatedProps;
};
const getDynamicProps = ({
  config,
  connection,
  dataset,
  schema,
  referencedSchemas,
  records
}, {
  formatter
}) => {
  const componentProps = [];
  if (Object.keys(connection.dynamicProperties).length) {
    for (const [name, connectionDescriptor] of Object.entries(connection.dynamicProperties)) {
      const result = dynamicPropsSetters[name]({
        config,
        dataset,
        schema,
        referencedSchemas,
        records,
        connectionDescriptor
      }, {
        formatter
      });
      if (result) {
        componentProps.push(result);
      }
    }
  }
  return componentProps;
};
const getExtraProps = connection => {
  const extraProps = [];
  for (const [name, value] of Object.entries(connection.extraData)) {
    extraProps.push([name, value]);
  }
  return extraProps;
};
const getFieldData = ({
  currentRecord,
  fieldName,
  schema,
  referencedSchemas
}) => {
  const [rootFieldName, nestedFieldName] = fieldName.split('.');
  if (nestedFieldName) {
    const referencedCollectionId = _schema.Schema.getReferencedCollectionId(schema, rootFieldName);
    const referencedSchema = referencedCollectionId ? referencedSchemas.find(({
      id
    }) => id === referencedCollectionId) : _schema.Schema.make();
    return makeFieldData({
      record: currentRecord[rootFieldName] ?? {},
      fieldName: nestedFieldName,
      schema: referencedSchema
    });
  }
  return makeFieldData({
    record: currentRecord,
    fieldName: rootFieldName,
    schema
  });
};
const makeFieldData = ({
  record,
  fieldName,
  schema
}) => ({
  value: _record.Record.getValue(record, fieldName),
  fieldType: _schema.Schema.getFieldType(schema, fieldName)
});
const makeLinkFromText = (fieldValue, extraData) => {
  // TODO: this old hack makes href without doublequotes, but this required in old viewer Text comp
  // rethink this solution
  const value = fieldValue ? fieldValue : '';
  return `<a href=${value} target="${(extraData == null ? void 0 : extraData.target) ?? '_blank'}"${extraData != null && extraData.rel ? ` rel="${extraData.rel}"` : ''} style="text-decoration: underline">${value}</a>`;
};
const formatValue = ({
  format: {
    type,
    params
  } = {},
  formatter,
  fieldType,
  value
}) => {
  if (type === DATETIME_FORMAT_TYPE && (dateFieldTypes.includes(fieldType) || _value.Value.isIsoString(value))) {
    return applyDateFormat({
      dateFormat: params.dateFormat,
      fieldType,
      value
    }, {
      formatter
    });
  }
  return applyDefaultFormat({
    fieldType,
    value
  });
};
const dateFieldTypes = [_wixDataSchemaTypes.FieldType.date, _wixDataSchemaTypes.FieldType.time, _wixDataSchemaTypes.FieldType.dateTime];
const applyDateFormat = ({
  dateFormat,
  fieldType,
  value
}, {
  formatter
}) => {
  const dateTimeValue = fieldType === _wixDataSchemaTypes.FieldType.time ? `1970-01-01T${value}Z` : value;
  const date = new Date(dateTimeValue);
  if (isNaN(date.getTime()) || fieldType === _wixDataSchemaTypes.FieldType.dateTime && !_value.Value.isIsoString(value)) {
    return value;
  }
  const timeZone = fieldType === _wixDataSchemaTypes.FieldType.date || fieldType === _wixDataSchemaTypes.FieldType.time ? 'UTC' : undefined;
  return formatter.formatDateTime(date.toISOString(), dateFormat, {
    timeZone
  });
};
const applyDefaultFormat = ({
  fieldType,
  value
}) => value && (fieldType === _wixDataSchemaTypes.FieldType.dateTime || _value.Value.isIsoString(value)) ? new Date(value).toString() : value;
const shouldSetValue = (config, value) => !(_config.Config.isLivePreviewEnabled(config) && _value.Value.isEmpty(value));
//# sourceMappingURL=render-data-to-components.js.map